import React, { useState } from "react";
import { Box, CircularProgress, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from "@mui/material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";

/**
 * Row actions (design item 14): the first `visible` actions stay as icons, the
 * rest open from ⋯. Pass `danger: true` for delete/refund — they sort last, in red.
 * Hidden actions (`show: false`) are dropped, so permission checks stay with the page.
 *
 * action: { label, icon, onClick, show?, disabled?, loading?, danger? }
 */
export default function RowActions({ actions, visible = 2 }) {
  const [anchor, setAnchor] = useState(null);
  const list = actions.filter((a) => a && a.show !== false);
  const safe = list.filter((a) => !a.danger);
  const risky = list.filter((a) => a.danger);
  const inline = safe.slice(0, visible);
  const more = [...safe.slice(visible), ...risky];

  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5, whiteSpace: "nowrap" }}>
      {inline.map((a) => (
        <Tooltip key={a.label} title={a.label}>
          <span>
            <IconButton
              size="small"
              aria-label={a.label}
              onClick={a.onClick}
              disabled={a.disabled || a.loading}
              sx={{ color: "#3B2A9F", "&:hover": { background: "#EDE9FE" } }}
            >
              {a.loading ? <CircularProgress size={18} /> : a.icon}
            </IconButton>
          </span>
        </Tooltip>
      ))}
      {more.length > 0 && (
        <>
          <Tooltip title="More actions">
            <IconButton
              size="small"
              aria-label="More actions"
              aria-haspopup="menu"
              onClick={(e) => setAnchor(e.currentTarget)}
              sx={{ color: "#374151", "&:hover": { background: "#F3F4F6" } }}
            >
              <MoreHorizIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={anchor}
            open={Boolean(anchor)}
            onClose={() => setAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            slotProps={{ paper: { sx: { minWidth: 200, borderRadius: 2 } } }}
          >
            {more.map((a) => (
              <MenuItem
                key={a.label}
                disabled={a.disabled || a.loading}
                onClick={() => {
                  setAnchor(null);
                  a.onClick?.();
                }}
                sx={{ fontSize: 14, color: a.danger ? "#B91C1C" : "#1F2937" }}
              >
                <ListItemIcon sx={{ color: a.danger ? "#B91C1C" : "#6B7280", "& svg": { fontSize: 20 } }}>
                  {a.loading ? <CircularProgress size={16} /> : a.icon}
                </ListItemIcon>
                <ListItemText primaryTypographyProps={{ fontSize: 14 }}>{a.label}</ListItemText>
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
    </Box>
  );
}
